import type * as G from '@/types/google'
import { ArcTypePriority } from '@/types/google'
import { useDatabase } from '@/composables/useDatabase'
import { useRegistryStore } from '@/stores/registry'
import { logger } from '@/utils/logger'
// import { useUserStore } from '@/stores/user'
// import { storeToRefs } from 'pinia'
// import { remove } from 'dexie'

const SUB_ACTION_TO_COMMENT_TYPE: Partial<Record<G.GActionSubtype, G.GCommentType>> = {
    COMMENT_POST: 'POST',
    COMMENT_ASSIGNMENT: 'ASSIGNMENT',
    COMMENT_SUGGESTION: 'SUGGESTION',
}

// type DeletedComment = Pick<G.GComment, 'folderId' | 'fileGid' | 'commentId'>
type DeletedReply = Pick<G.GReply, 'fileGid' | 'commentId' | 'replyId'>

export function useFolderUpdates () {
    let folder: G.Folder
    let updates: G.FolderUpdatesWithRingMap
    const actorsUpdates: G.ActorsUpdatesMap = new Map()

    const deletedCommentsMap: Map<G.GoogleItemId, G.CommentId[]> = new Map()
    const deletedReplies: DeletedReply[] = []

    let gfile: G.GFile
    let gaction: G.GFileAction
    let commentId: G.CommentId

    // currently processed comment and reply
    let comment: G.GComment
    let reply: G.GReply

    //
    const commentsMap: G.GCommentsMap = new Map()
    const repliesMap: G.GRepliesMap = new Map()

    /**
     * Process a COMMENT action: resolve the GComment from the in-memory map,
     * Dexie cache, or a blank template — then update it based on the action data.
     *
     * Rules:
     * - `firstAssignor` is set once, on the first COMMENT_ASSIGNMENT action seen for the comment.
     * - `repliesTotal` / `repliesNew` are incremented for every REPLY_ADDED action.
     */
    async function processCommentAction (f: G.GFile, a: G.GFileAction): Promise<void> {
        gfile = f
        gaction = a

        if (!gaction.comment) {
            return
        }

        // get folderId + fileGid + commentId to uniquely identify the comment to extract from dexie
        commentId = gaction.comment.commentId

        // the comment (parent) is always required
        await getExistingOrGhostComment()

        // happens when the comment creation is found in Activity API,
        // ghost comments are created above
        if (isNewComment()) {
            processNewComment()
        }

        // Note: The comment itself is treated as a reply for processing purposes (FileRing)
        // but it is not actually a reply in the traditional sense
        // and is showing up in the replies list and is removed during `commitReplies`
        await processReply()
    }

    async function getExistingOrGhostComment () {
        const fileCommentsMap = getFileCommentsMap(gfile.gid)

        const t = fileCommentsMap.get(commentId)
        if (t) {
            comment = t
            return
        }

        // Try Dexie first
        const db = useDatabase()
        const dbComment = await db.comments.get([gfile.folderId, gfile.gid, commentId])

        if (dbComment) {
            comment = dbComment

            // Reset arcType, actor and reply count
            if (folder.markedAsRead) {
                comment.arcType = comment.watching ? 'WATCH' : 'OTHER'
                comment.arcModifiedTs = gaction.ts
                comment.actorsInFromTo = []
                comment.repliesTotalInFromTo = 0
            }
        } else {
            createGhostComment()
        }

        fileCommentsMap.set(commentId, comment)
    }

    function processNewComment () {
        comment.createdTs = gaction.ts
        comment.needsActivityFetch = false // because the 'ADD' event happens during sync
        comment.ghost = false // we now have the real 'ADDED' activity for this comment thread

        comment.type = action2CommentType()

        comment.actor = gaction.actor

        if (comment.type === 'ASSIGNMENT') {
            // it might already be set
            if (!comment.currentAssignor) {
                comment.currentAssignor = comment.actor
            }

            comment.assignee = gaction.comment!.assignedTo ?? undefined // comment.actor

            if (!comment.currentAssignee) {
                comment.currentAssignee = comment.assignee
            }
        }

        comment.mentioned = gaction.comment!.mentioned

        /*  TODO: review if this code is needed
        // if (comment.modifiedTs <= gaction.ts) {
        if (gaction.ts < folder.fromTs) {
            comment.arcType = 'OTHER'
            comment.lastActionType = gaction.comment!.type
        }
        */

        // it may already contain replies for Ghost comments
        // comment.repliesTotal = 0

        comment.mentionsMe = comment.mentioned?.includes('ME') ?? false

        // automatically subscribe to the comment if mentioned or assigned
        if (!comment.watching && gaction.ts >= comment.watchingTs) {
            comment.watching = comment.actor === 'ME'
                || comment.assignee === 'ME'
                || comment.mentionsMe

            comment.watchingTs = gaction.ts

            if (comment.watching) {
                comment.arcType = 'WATCH'
                comment.arcModifiedTs = gaction.ts
            }
        }
    }

    /**
     * Ghost Comments are created if the [fromTs - toTs] range of the folder sync
     * does **NOT** include the creation action of the comment.
     */
    function createGhostComment () {
        comment = getBlankComment()

        comment.repliesTotal = 1 // the reply that triggered the creation of this ghost comment
        comment.arcType = 'OTHER'

        // TODO: comment.createdTs = gaction.ts // but this is for sure an earlier date
    }

    function getBlankComment (): G.GComment {
        const oneMinuteSinceCreation = folder.fromTs - 60 * 1000

        const blankComment: G.GComment = {
            folderId: gfile.folderId,
            fileGid: gfile.gid,
            commentId: gaction.comment!.commentId,
            type: 'POST',
            arcType: 'OTHER',
            arcModifiedTs: gaction.ts,
            // arcForMe: false,

            watching: false,
            watchingTs: 0,
            modifiedTs: gaction.ts,
            modifiedApiTs: gaction.ts,
            createdTs: oneMinuteSinceCreation, // the comment is not in DB!!!
                                               // we set an older date for creation
            needsActivityFetch: true,  // STEP 1: DriveAPI COUNT(zombieComments) calls: to get the createdTs for all comments
                                       // STEP 2: DriveActivityAPI.query (min/max createdTs for comments) & filter: 'detail.action_detail_case: COMMENT' to get the assignor/assignee
                                       // STEP 3: update comments in DB with the real createdTs + assignor + assigned info
            ghost: true, // no 'ADDED' activity seen yet for this comment thread, only a reply
            lastActionType: gaction.comment!.type,
            lastReassignedTs: 0,
            lastReopenedTs: 0,
            open: true,
            deleted: false,
            currentAssignor: undefined,
            currentAssignee: undefined,
            actor: 'UNKNOWN',
            assignee: undefined,
            resolver: undefined,
            mentioned: undefined,
            mentionsMe: false,
            repliesTotal: 0,
            repliesTotalInFromTo: 0,
            actorsInFromTo: [],
            fileIcon: gaction.icon,
            fileTitle: gaction.title,
        }

        if (gaction.subAction === 'COMMENT_ASSIGNMENT') {
            blankComment.type = 'ASSIGNMENT'
        }

        return blankComment
    }

    function processFileCreateDeleteRestore (f: G.GFile, a: G.GFileAction): void {
        gfile = f
        gaction = a

        if (!['DELETE', 'RESTORE', 'CREATE'].includes(gaction.action)) {
            return
        }

        if (!actorsUpdates.has(gaction.actor)) {
            actorsUpdates.set(gaction.actor, {
                actorId: gaction.actor,
                newFiles: 0,
                delFiles: 0,
                posts: 0,
                ring: [],
                ringMap: new Map(),
                ts: gaction.ts,
            })
        }

        if (gaction.action === 'DELETE') {
            updates.delFiles++
            actorsUpdates.get(gaction.actor)!.delFiles++
        } else {
            updates.newFiles++
            actorsUpdates.get(gaction.actor)!.newFiles++
        }
    }

    function isNewComment (): boolean {
        return gaction.comment!.type === 'ADDED'
    }

    function action2CommentType (): G.GCommentType {
        return SUB_ACTION_TO_COMMENT_TYPE[gaction.subAction ?? 'OTHER'] ?? 'POST'
    }

    async function processReply () {
        const actionType = gaction.comment!.type

        // comment.dataFetchTs >= gaction.ts means Drive API comments.get already
        // ran past this action's time, so this reply may already exist in Dexie
        // as a ghost reply
        const existingReply = (comment.dataFetchTs ?? 0) >= gaction.ts
            ? await useDatabase().replies.get([gfile.folderId, gfile.gid, commentId, gaction.comment!.replyId!])
            : undefined

        reply = existingReply ?? {
            folderId: gfile.folderId,
            fileGid: gfile.gid,
            commentId,
        } as G.GReply

        reply.ghost = false
        reply.actor = gaction.actor

        reply.replyId = gaction.comment!.replyId! || String(gaction.ts)
        reply.replyIdOriginal = gaction.comment!.replyIdOriginal ?? ''
        reply.deleted = false
        reply.actionType = actionType
        reply.assignee = gaction.comment!.assignedTo
        reply.createdTs = gaction.ts
        reply.mentionsMe = gaction.comment!.mentioned?.includes('ME') ?? false

        const watchOnReply = await useRegistryStore().getWatchOnReply()

        if (reply.actor === 'ME' && gaction.ts >= comment.watchingTs
            && (actionType === 'ADDED' || watchOnReply === true)  // always watch if new comment
        ) {
            comment.watching = true
            comment.watchingTs = gaction.ts
            setArcTypeForComment('WATCH')
        }

        if (reply.mentionsMe && gaction.ts >= comment.watchingTs) {
            comment.watching = true
            comment.watchingTs = gaction.ts
            setArcTypeForComment('MENTION_ME')
        }

        updateFromToStats(actionType)

        switch (actionType) {
            case 'ADDED': {
                newComment()
                break
            }
            case 'DELETED': {
                delComment()
                break
            }
            case 'REPLY_ADDED': {
                addReply()
                break
            }
            case 'REPLY_DELETED': {
                delReply()
                break
            }
            case 'RESOLVED': {
                resolveComment()
                break
            }
            case 'REOPENED': {
                reopenComment()
                break
            }
            case 'REASSIGNED': {
                reassignComment()
                break
            }
            case 'ACCEPTED': {
                acceptSuggestion()
                break
            }
            case 'REJECTED': {
                rejectSuggestion()
                break
            }
            case 'ACCEPT_DELETED': {
                acceptDeleteSuggestion()
                break
            }
            case 'REJECT_DELETED': {
                rejectDeleteSuggestion()
                break
            }
            case 'UNSPECIFIED': {
                break
            }
        }

        if (comment.watching) {
            setArcTypeForComment('WATCH')
        }

        // prevent comment update for Ghost comments
        if (reply.createdTs > comment.modifiedTs) {
            comment.fileIcon = gaction.icon
            comment.fileTitle = gaction.title

            comment.lastActionType = actionType

            // update the comment's modifiedTs to the latest reply time
            // it will be used to fetch comments for [folder.fromTs, folder.toTs] interval
            comment.modifiedTs = reply.createdTs
        }

        if (actionType !== 'REPLY_DELETED') {
            // update the repliesMap
            if (!repliesMap.has(gfile.gid)) {
                repliesMap.set(gfile.gid, new Map())
            }

            const fileRepliesMap = repliesMap.get(gfile.gid)!
            if (!fileRepliesMap.has(commentId)) {
                fileRepliesMap.set(commentId, [])
            }

            const replies = fileRepliesMap.get(commentId)!

            replies.push(reply)

            /* there are no reply EDITs, only APPEND and DELETE, so we don't need to check
            for existing reply in the map
            const existingReplyIndex = replies.findIndex(r => r.replyId === reply.replyId)
            if (existingReplyIndex !== -1) {
                replies[existingReplyIndex] = reply
            } else {
                replies.push(reply)
            }
            */
        }
    }

    function updateFromToStats (actionType: G.GReply['actionType']) {
        if (gaction.ts < folder.fromTs) {
            return
        }

        switch (actionType) {
            case 'ADDED':
            case 'REPLY_ADDED':
            case 'RESOLVED':
            case 'REOPENED':
            case 'REASSIGNED': {
                comment.repliesTotalInFromTo++
                if (!comment.actorsInFromTo.includes(reply.actor)) {
                    comment.actorsInFromTo.push(reply.actor)
                }
                break
            }
            case 'REPLY_DELETED': {
                comment.repliesTotalInFromTo = Math.max(0, comment.repliesTotalInFromTo - 1)
                comment.actorsInFromTo = comment.actorsInFromTo.filter(a => a !== reply.actor)
                break
            }
        }
    }

    function newComment () {
        let arcType: G.ArcType = 'OTHER' // default to OTHER for regular comments

        if (comment.type === 'ASSIGNMENT' && comment.assignee === 'ME') {
            switch (comment.lastActionType) {
                case 'ADDED': {
                    arcType = 'ASSIGNMENT_NEW_ME'
                    break
                }
                case 'REOPENED': {
                    arcType = 'ASSIGNMENT_REOPEN_ME'
                    break
                }
                case 'RESOLVED': {
                    arcType = 'ASSIGNMENT_RESOLVED_ME'
                    break
                }
            }
        }

        // if (comment.type === 'SUGGESTION') arcType = 'SUGGESTION'

        // // don't update arc for Ghost comments
        // if (gaction.ts < comment.lastReassignedTs
        //     || gaction.ts < comment.lastReopenedTs
        // ) {
        //     return
        // }

        setArcTypeForComment(arcType, true)
        // setCurrentArcTypeForComment(arcType)
    }

    function delComment () {
        comment.deleted = true
        if (!deletedCommentsMap.has(comment.fileGid)) {
            deletedCommentsMap.set(comment.fileGid, [])
        }
        deletedCommentsMap.get(comment.fileGid)!.push(comment.commentId)

        // delete IN MEMORY, it will be deleted from DB later in bulk
        commentsMap.get(comment.fileGid)?.delete(comment.commentId)
    }

    function addReply () {
        comment.repliesTotal++
    }

    function delReply () {
        reply.deleted = true
        deletedReplies.push({
            // folderId: reply.folderId,
            fileGid: reply.fileGid,
            commentId: reply.commentId,
            replyId: reply.replyId,
        })
        comment.repliesTotal = Math.max(0, comment.repliesTotal - 1)
    }

    function resolveComment () {
        // resolved status is ignored before the folder's fromTs
        if (!folder.markedAsRead && comment.modifiedTs < folder.fromTs) {
            return
        }

        comment.open = false
        comment.resolver = gaction.actor

        if (comment.currentAssignee === 'ME' && gaction.ts >= comment.watchingTs) {
            comment.watching = true
            comment.watchingTs = gaction.ts
            setArcTypeForComment('ASSIGNMENT_RESOLVED_ME', true)
        }
    }

    function reopenComment () {
        if (!folder.markedAsRead && gaction.ts < comment.lastReopenedTs) {
            return
        }

        comment.open = true
        comment.lastReopenedTs = gaction.ts
        comment.currentAssignor = gaction.actor // the one who reopened becomes the new assignor

        if (comment.currentAssignee === 'ME' && gaction.ts >= comment.watchingTs) {
            comment.watching = true
            comment.watchingTs = gaction.ts
            setArcTypeForComment('ASSIGNMENT_REOPEN_ME', true)
        }
    }

    function reassignComment () {
        // if (gaction.ts < folder.fromTs) {
        //     return
        // }

        if (!folder.markedAsRead && gaction.ts < comment.lastReassignedTs) {
            return
        }

        // for Ghost comments
        comment.type = 'ASSIGNMENT'

        comment.lastReassignedTs = gaction.ts
        comment.currentAssignor = gaction.actor
        comment.currentAssignee = gaction.comment!.assignedTo

        // auto-watch if I'm the new assignee
        if (comment.currentAssignee === 'ME') {
            if (gaction.ts >= comment.watchingTs) {
                comment.watching = true
                comment.watchingTs = gaction.ts
                setArcTypeForComment('ASSIGNMENT_NEW_ME', true)
            }
        } else {
            if (comment.watching) {
                setArcTypeForComment('WATCH', true)
            } else {
                setArcTypeForComment('OTHER', true)
            }
        }
    }

    // TODO: implement SUGGESTION accept/reject actions
    function acceptSuggestion () {}
    function rejectSuggestion () {}
    function acceptDeleteSuggestion () {}
    function rejectDeleteSuggestion () {}

    function getComment (fileGid: G.GoogleItemId, commentId: G.CommentId): G.GComment {
        return commentsMap.get(fileGid)!.get(commentId)!
    }

    /**
     * Ensure the per-file comment map exists and return it.
     */
    function getFileCommentsMap (fileGid: G.GoogleItemId): G.GFileCommentsMap {
        if (!commentsMap.has(fileGid)) {
            commentsMap.set(fileGid, new Map())
        }

        return commentsMap.get(fileGid)!
    }

    /**
     * Convert an AvatarRing array into a Map keyed by gid.
     * Shared by both folderList2Map and actorsList2Map (DRY).
     */
    function avatarRing2Map (ring: G.AvatarArc[]): Map<G.GoogleItemId, G.AvatarArc> {
        const map = new Map<G.GoogleItemId, G.AvatarArc>()
        for (const r of ring) {
            map.set(r.gid, r)
        }
        return map
    }

    function fileRing2Map (ring: G.FileArc[]): Map<G.CommentId, G.FileArc> {
        const map = new Map<G.CommentId, G.FileArc>()
        for (const r of ring) {
            if (r.commentId !== undefined) {
                map.set(r.commentId, r)
            }
        }
        return map
    }

    // const folderUpdates: G.FolderUpdatesWithRingMap = {
    //     newFiles: 0,
    //     delFiles: 0,
    //     posts: 0,
    //     ring: [],
    //     ringMap: new Map(),
    // }

    /**
     * Populate `folderUpdates` from `folder.updates`, converting the `rings`
     * array into a `ringsMap` keyed by gid.
     */
    function initFolder () {
        updates = { ...folder.updates, ringMap: avatarRing2Map(folder.updates.ring ?? []) }

        // const { newFiles, delFiles, posts, ring } = folder.updates
        // folderUpdates.newFiles = newFiles
        // folderUpdates.delFiles = delFiles
        // folderUpdates.posts = posts
        // folderUpdates.ring = ring
        // folderUpdates.ringMap = avatarRing2Map(ring)
    }

    /**
     * Populate `actorUpdates` from `folder.actorsUpdates`, converting each
     * actor's `rings` array into a `ringsMap` keyed by gid.
     */
    function initActorUpdates () {
        actorsUpdates.clear()
        for (const u of folder.actorsUpdates) {
            // actorsUpdates.set(u.actorId, u)
            actorsUpdates.set(u.actorId, {
                ...u,
                ringMap: avatarRing2Map(u.ring ?? []),
            })
        }
    }

    function initVars (d: G.Folder) {
        folder = d

        // folderUpdates = {
        commentsMap.clear()
        repliesMap.clear()

        deletedCommentsMap.clear()
        deletedReplies.length = 0

        initFolder()
        initActorUpdates()
        // filesList2Map() this happens when files are fetched
    }

    function computeCommentUpdates () {
        for (const fileRepliesMap of repliesMap.values()) {
            for (const commentReplies of fileRepliesMap.values()) {
                for (const reply of commentReplies) {
                    updates.posts++
                    updates.ts = reply.createdTs

                    // Folder Ring
                    updateActorOrFolderRing(updates.ringMap, reply)

                    if (!actorsUpdates.has(reply.actor)) {
                        actorsUpdates.set(reply.actor, {
                            actorId: reply.actor,
                            newFiles: 0,
                            delFiles: 0,
                            posts: 0,
                            ring: [],
                            ringMap: new Map(),
                            ts: reply.createdTs,
                        })
                    }

                    const a = actorsUpdates.get(reply.actor)
                    a!.ts = reply.createdTs
                    a!.posts++

                    // User Ring
                    updateActorOrFolderRing(a!.ringMap, reply)
                }
            }
        }
    }

    function updateActorOrFolderRing (ringMap: Map<G.GoogleItemId, G.AvatarArc>, reply: G.GReply) {
        let existingArc = ringMap.get(reply.fileGid)
        const c = getComment(reply.fileGid, reply.commentId)

        if (c === undefined || c.type === undefined) {
            // TODO: handle the case where the comment for a reply is undefined
            // console.log('undefined comment for reply', reply.commentId, ', in file:', reply.fileGid)
            // console.log(JSON.stringify(reply, null, 2))

            return
        }

        if (!existingArc) {
            existingArc = {
                gid: reply.fileGid,
                type: 'OTHER',
                forMe: isCommentForMe(c), // reply.mentionsMe || c.watching, // if I'm watching the comment thread, I want to see REPLY arcs for that file even if I'm not mentioned in the replies
                ts: reply.createdTs,
            }
            ringMap.set(reply.fileGid, existingArc)
        }

        updateExistingAvatarArc(existingArc, reply)
    }

    function isCommentForMe (comment: G.GComment): boolean {
        return comment.watching
    }

    function computeFileUpdates (f: G.GFile) {
        gfile = f

        removeDeletedCommentsFromFileRing()

        // no comments for the file, nothing to update in the ring
        const fileCommentsMap = commentsMap.get(gfile.gid)
        if (!fileCommentsMap) {
            return
        }

        const ringMap = fileRing2Map(gfile.updates.ring)

        for (const c of fileCommentsMap.values()) {
            // is this comment in `deletedCommentsMap` and is expected to be already removed from ringMap
            if (c.deleted) {
                continue
            }

            const existingArc = ringMap.get(c.commentId)
            if (existingArc) {
                updateExistingFileArc(existingArc, c)
            } else {
                gfile.updates.ring.push({
                    commentId: c.commentId,
                    type: c.arcType,
                    forMe: isCommentForMe(c),
                    ts: c.arcModifiedTs, // c.modifiedTs ?? c.createdTs,
                })
            }

            const fileRepliesMap = repliesMap.get(gfile.gid)
            if (fileRepliesMap) {
                const commentReplies = fileRepliesMap.get(c.commentId)
                if (commentReplies) {
                    gfile.updates.posts += commentReplies.length
                }
            }
        }

        gfile.updates.ring = getSortedFileRing(gfile.updates.ring)

        gfile.updates.forMe = gfile.updates.ring.some(ring => ring.forMe)

        // TODO: discard very old comments? (resolved/closed or just comments with no activity for a long time)
        // more precisely: discard "grey" arcs because it has lowest arc priority
    }

    /*
    function getArcTypeFromReply (reply: G.GReply): G.ArcType {
        switch (c?.type) {
            case 'ASSIGNMENT': {
                return 'ASSIGNMENT'
                break
            }
            case 'SUGGESTION': {
                return 'SUGGESTION'
                break
            }
            default: {
                return reply.mentionsMe ? 'MENTION' : 'REPLY'
            }
        }
    }
        */

    function updateExistingAvatarArc (arc: G.AvatarArc, reply: G.GReply) {
        // if (arc.forMe === true && !reply.mentionsMe) {
        //     return // keep the current arc type
        // }

        const c = getComment(reply.fileGid, reply.commentId)

        // if (reply.actor == 'ME' && reply.actionType === 'RESOLVED') {
        //     logger.warn(`Reply by ME resolved: ${JSON.stringify(reply)}`)
        //     logger.warn(`Comment associated with the reply: ${JSON.stringify(c)}`)
        // }

        let arcType: G.ArcType = 'OTHER' // default to REPLY for regular comments

        if (c.watching) {
            arc.forMe = true
            arcType = 'WATCH'
        }

        if (reply.mentionsMe) {
            arc.forMe = true
            arcType = 'MENTION_ME'
        }

        switch (reply.actionType) {
            case 'ADDED': {
                if (c.type === 'ASSIGNMENT' && c.assignee === 'ME') {
                    arcType = 'ASSIGNMENT_NEW_ME'
                    arc.forMe = true
                }
                // else if (c.type === 'SUGGESTION') {
                //     arcType = 'SUGGESTION'
                // }
                break
            }
            case 'REASSIGNED': {
                if (reply.assignee === 'ME') {
                    arcType = 'ASSIGNMENT_NEW_ME'
                    arc.forMe = true
                }
                break
            }
            case 'REOPENED': {
                if (c.type === 'ASSIGNMENT' && c.assignee === 'ME') {
                    arcType = 'ASSIGNMENT_REOPEN_ME'
                    arc.forMe = true
                }
                break
            }
            case 'RESOLVED': {
                if (c.type === 'ASSIGNMENT' && c.assignee === 'ME') {
                    arcType = 'ASSIGNMENT_RESOLVED_ME'
                    arc.forMe = true
                }
                break
            }

            // TODO: Implement SUGGESTION accept/reject actions and update arcType accordingly
        }

        // set based on priority
        if (ArcTypePriority[arcType] > ArcTypePriority[arc.type]) {
            arc.type = arcType
        }

        arc.ts = reply.createdTs
    }

    function setArcTypeForComment (arcType: G.ArcType, force = false) {
        if (arcType === 'WATCH' && comment.arcType === 'OTHER') {
            comment.arcType = 'WATCH'
        }

        // actions BEFORE `fromTs` has no effect
        if (gaction.ts < folder.fromTs) {
            return
        }

        /* TODO: this should be removed
        // don't update arc for Ghost comments
        if (comment.modifiedTs < folder.fromTs) {
            return
        }
        */

        // // arcType has already been updated by a newer action, skip
        // if (comment.arcModifiedTs > gaction.ts) {
        //     return
        // }

        // overwrite comment arc if the reply is newer than the folder's last sync time
        // this ensures that in File View, a comment is "colored" only if there were
        // assign/reopen/reassign during sync interval

        // if (comment.arcModifiedTs < folder.fromTs) {
        //     comment.arcType = arcType
        //     comment.arcModifiedTs = gaction.ts
        //     return
        // }

        // set arcType to the most recent action (if it's not a MENTION or OTHER)
        if (ArcTypePriority[arcType] >= 3 && comment.arcModifiedTs <= gaction.ts) {
        // if (arcType !== 'OTHER' ) {
            comment.arcType = arcType
            comment.arcModifiedTs = gaction.ts
            return
        }

        // for actions during current sync phase, use priority
        if (ArcTypePriority[arcType] > ArcTypePriority[comment.arcType]) {  // || force
        // if (arcType !== 'OTHER' ) {
            comment.arcType = arcType
            comment.arcModifiedTs = gaction.ts
        }

        /**
        // override the topArcType to REPLY if I'm watching the comment thread,
        // even if the latest action is a new assignment or suggestion,
        // to make sure the user doesn't miss any updates in threads they're following
        if (comment.topArcForMe === false && comment.watching) {
            comment.topArcForMe = true
            comment.topArcType = arcType
            return
        } else if (comment.topArcForMe === true && !comment.watching) {
            return // keep the current topArcType
        }

        // set based on priority
        if (ArcTypePriority[arcType] > ArcTypePriority[comment.topArcType]) {
            comment.topArcType = arcType
        }
        */
    }

    /**
     * A simple function to update the File Arc that completely mirrors the `comment` state
     */
    function updateExistingFileArc (arc: G.FileArc, comment: G.GComment) {
        arc.type = comment.arcType
        arc.forMe = isCommentForMe(comment)
        arc.ts = comment.arcModifiedTs

        /**
        if (comment.arcModifiedTs < folder.fromTs) {
            arc.arcType = comment.arcType
            arc.ts = comment.arcModifiedTs
            return
        }

        // set based on priority
        if (ArcTypePriority[comment.arcType] > ArcTypePriority[arc.type]) {
            arc.type = comment.arcType
            arc.ts = comment.arcModifiedTs ?? comment.createdTs
        }
        */
    }

    /**
     * Mirror `comments` onto the file's existing arcs, then recompute the ring order and `forMe`.
     * Comments without an existing arc are ignored.
     * @returns whether any arc was updated
     */
    function updateFileArcsFromComments (f: G.GFile, comments: G.GComment[]): boolean {
        const ringMap = fileRing2Map(f.updates.ring)
        let updated = false

        for (const c of comments) {
            const existingArc = ringMap.get(c.commentId)
            if (!c.deleted && existingArc) {
                updateExistingFileArc(existingArc, c)
                updated = true
            }
        }

        if (updated) {
            f.updates.ring = getSortedFileRing(f.updates.ring)
            f.updates.forMe = f.updates.ring.some(ring => ring.forMe)
        }

        return updated
    }

    function removeDeletedCommentsFromFileRing () {
        const deletedCommentIds = deletedCommentsMap.get(gfile.gid)
        if (deletedCommentIds && deletedCommentIds.length > 0) {
            const deletedSet = new Set(deletedCommentIds)
            gfile.updates.ring = gfile.updates.ring.filter(r => !r.commentId || !deletedSet.has(r.commentId))
        }
    }

    async function commitComments () {
        const db = useDatabase()
        await removeDeletedCommentsFromDB()
        await db.comments.bulkPut(Array.from(commentsMap.values()).flatMap(m => Array.from(m.values())))
    }

    async function commitReplies () {
        const db = useDatabase()

        await db.replies.bulkPut(Array.from(repliesMap.values()).flatMap(m => Array.from(m.values()).flat()))
        await removeDeletedRepliesFromDB()
    }

    // Deleted comments can't be restored in Google Drive, and therefore
    // should be removed from the local database to keep it in sync.
    async function removeDeletedCommentsFromDB () {
        const db = useDatabase()

        for (const [fileGid, commentIds] of deletedCommentsMap.entries()) {
            const keys = commentIds.map(commentId => [folder.id!, fileGid, commentId] as [number, string, string])
            await db.comments.bulkDelete(keys)
        }
    }

    async function removeDeletedRepliesFromDB () {
        const db = useDatabase()

        // delete explicitly deleted replies
        const keys = deletedReplies.map(r => [folder.id!, r.fileGid, r.commentId, r.replyId] as [number, string, string, string])
        await db.replies.bulkDelete(keys)

        // delete all replies belonging to deleted comments
        for (const [fileGid, commentIds] of deletedCommentsMap.entries()) {
            for (const commentId of commentIds) {
                await db.replies
                    .where('[folderId+fileGid+commentId]')
                    .equals([folder.id!, fileGid, commentId])
                    .delete()
            }
        }
    }

    /**
     * Convert a ring map to a sorted array: `forMe=true` items first,
     * then by descending `ArcTypePriority`.
     */
    function getSortedAvatarRing (ringMap: Map<G.GoogleItemId, G.AvatarArc>): G.AvatarArc[] {
        return Array.from(ringMap.values())
            .sort((a: G.AvatarArc, b: G.AvatarArc) => {
                // if (a.forMe !== b.forMe) {
                //     return a.forMe ? -1 : 1
                // }
                return ArcTypePriority[b.type] - ArcTypePriority[a.type]
            })
    }

    /**
     * Sort a file's comment arcs by relevance, arc priority, and recency.
     */
    function getSortedFileRing (ring: G.FileArc[]): G.FileArc[] {
        return ring.sort((a: G.FileArc, b: G.FileArc) => {
            if (a.forMe !== b.forMe) {
                return a.forMe ? -1 : 1
            }

            const priorityDifference = ArcTypePriority[b.type] - ArcTypePriority[a.type]
            if (priorityDifference !== 0) {
                return priorityDifference
            }

            return b.ts - a.ts
        })
    }

    /**
     * Sort actors by:
     * 1. Actors with at least one `forMe=true` arc — sorted by their highest-priority
     *    forMe arc type; ties broken by count of arcs of that same type (more = first).
     * 2. Actors with only `forMe=false` arcs — sorted by total arc count (more = first).
     * 3. Actors with no arcs — last.
     */
    function sortActorsUpdatesList (list: G.ActorsUpdatesList): G.ActorsUpdatesList {
        return list.sort((a: G.ActorUpdates, b: G.ActorUpdates) => {
            // Group 3: empty ring → last
            if (a.ring.length === 0 && b.ring.length === 0) {
                return 0
            }
            if (a.ring.length === 0) {
                return 1
            }
            if (b.ring.length === 0) {
                return -1
            }

            // sort by highest-priority arc first
            const aTopRing = a.ring.reduce((topRing, ring) =>
                ArcTypePriority[ring.type] > ArcTypePriority[topRing.type] ? ring : topRing,
            )
            const bTopRing = b.ring.reduce((topRing, ring) =>
                ArcTypePriority[ring.type] > ArcTypePriority[topRing.type] ? ring : topRing,
            )
            if (aTopRing.type !== bTopRing.type) {
                return ArcTypePriority[bTopRing.type] - ArcTypePriority[aTopRing.type]
            }

            // At this point, aTopPriority === bTopPriority,
            // so we fall back to recency.
            return bTopRing.ts - aTopRing.ts

            /**
            const aForMeArcs = a.ring.filter((r: G.AvatarArc) => r.forMe)
            const bForMeArcs = b.ring.filter((r: G.AvatarArc) => r.forMe)
            const aHasForMe = aForMeArcs.length > 0
            const bHasForMe = bForMeArcs.length > 0

            // Group 1 before Group 2
            if (aHasForMe !== bHasForMe) {
                return aHasForMe ? -1 : 1
            }

            if (aHasForMe) {
                // Both in Group 1: sort by highest-priority forMe arc
                const aTopPriority = Math.max(...aForMeArcs.map((r: G.AvatarArc) => ArcTypePriority[r.type]))
                const bTopPriority = Math.max(...bForMeArcs.map((r: G.AvatarArc) => ArcTypePriority[r.type]))
                if (aTopPriority !== bTopPriority) {
                    return bTopPriority - aTopPriority
                }
                // Tie: same top ArcType (priorities are unique per type) → more arcs of that type first
                const topType = aForMeArcs.find((r: G.AvatarArc) => ArcTypePriority[r.type] === aTopPriority)!.type
                return b.ring.filter((r: G.AvatarArc) => r.type === topType).length
                    - a.ring.filter((r: G.AvatarArc) => r.type === topType).length
            }

            // Both in Group 2: sort by total arc count
            return b.ring.length - a.ring.length
            */
        })
    }

    /**
     * Serialize internal ring-map state into storable `FolderUpdates` and a
     * priority-sorted `ActorsUpdatesList` (ringMap stripped from both).
     */
    function getFolderAndActorUpdates (): { folderUpdates: G.FolderUpdates, actorsUpdates: G.ActorsUpdatesList } {
        const folderUpdatesResult: G.FolderUpdates = {
            newFiles: updates.newFiles,
            delFiles: updates.delFiles,
            posts: updates.posts,
            ring: getSortedAvatarRing(updates.ringMap),
            ts: updates.ts,
        }

        // Display actors in order of priority: people with actions involving "ME", are first
        // then by total number of actions, then by alphabetical order of displayName (or actorId if no displayName)
        const actorsUpdatesList: G.ActorsUpdatesList = sortActorsUpdatesList(
            Array.from(actorsUpdates.values())
                .map(({ ringMap, ...rest }): G.ActorUpdates => ({
                    ...rest,
                    ring: getSortedAvatarRing(ringMap),
                })),
        )

        return { folderUpdates: folderUpdatesResult, actorsUpdates: actorsUpdatesList }
    }

    return {
        gCommentsMap: commentsMap,
        processCommentAction,
        initVars,
        computeFileUpdates,
        updateFileArcsFromComments,
        computeCommentUpdates,
        processFileCreateDeleteRestore,
        commitComments,
        commitReplies,
        getFolderAndActorUpdates,
    }
}
